/**
 * 看板 - 总览数据
 * 关键指标 + 用户增长 + 收入趋势 + 实时活动
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, UserPlus, Crown, CreditCard, TrendingUp, Activity, DollarSign,
  AlertCircle, Database, Clock, ArrowUp, ArrowDown, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

export function DashboardView() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  async function load(silent = false) {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [s, u, p, l] = await Promise.all([
        api.admin.stats(),
        api.admin.users({ pageSize: 5 }),
        api.admin.payments({ pageSize: 5 }),
        api.admin.logs({ pageSize: 8 }),
      ]);
      setStats(s.stats);
      setUsers(u.users || []);
      setPayments(p.payments || []);
      setLogs(l.logs || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1,2,3].map(i => (
          <div key={i} className="card h-24 animate-pulse bg-white/2" />
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="card text-center py-12 text-ink-3">
        <AlertCircle size={32} className="mx-auto mb-2 opacity-50" />
        <div className="text-sm">加载失败</div>
        <button onClick={() => load()} className="mt-3 btn-ghost text-xs">重试</button>
      </div>
    );
  }

  // 计算试用转化率
  const trialConversion = stats.trialExpired + stats.activeUsers > 0
    ? (stats.activeUsers / (stats.trialExpired + stats.activeUsers) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-4">
      {/* 关键指标 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <MetricCard
          icon={Users}
          label="总用户"
          value={stats.totalUsers}
          color="text-brand-cyan"
          bg="from-brand-cyan/20 to-brand-cyan/5"
        />
        <MetricCard
          icon={UserPlus}
          label="活跃用户"
          value={stats.activeUsers}
          color="text-brand-purple"
          bg="from-brand-purple/20 to-brand-purple/5"
          trend="+12%"  // TODO: 真实计算
        />
        <MetricCard
          icon={Crown}
          label="付费用户"
          value={stats.tierCounts?.reduce((s, t) => s + t.c, 0) - stats.onTrial}
          color="text-brand-gold"
          bg="from-brand-gold/20 to-brand-gold/5"
          sub={`试用转化 ${trialConversion}%`}
        />
        <MetricCard
          icon={DollarSign}
          label="总收入"
          value={`¥${stats.revenue.total.toFixed(0)}`}
          color="text-brand-cyan"
          bg="from-brand-cyan/20 to-brand-purple/5"
          sub={`今日 ¥${stats.revenue.today.toFixed(0)}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        {/* 试用状态 */}
        <div className="card !p-3">
          <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Clock size={12} className="text-brand-gold" />
            试用状态
          </h3>
          <div className="space-y-1.5">
            <ProgressBar
              label="试用中"
              value={stats.onTrial}
              total={stats.totalUsers}
              color="bg-brand-cyan"
            />
            <ProgressBar
              label="已过期"
              value={stats.trialExpired}
              total={stats.totalUsers}
              color="bg-accent-danger"
            />
            <ProgressBar
              label="已转化"
              value={(stats.tierCounts?.reduce((s, t) => s + t.c, 0) || 0) - stats.onTrial - stats.trialExpired}
              total={stats.totalUsers}
              color="bg-brand-gold"
            />
          </div>
        </div>

        {/* 会员分布 */}
        <div className="card !p-3">
          <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <Crown size={12} className="text-brand-purple" />
            会员分布
          </h3>
          <div className="space-y-1.5">
            {(stats.tierCounts || []).map(t => (
              <div key={t.tier} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <div className={clsx(
                    'w-1.5 h-1.5 rounded-full',
                    t.tier.includes('MAX') ? 'bg-brand-gold' :
                    t.tier.includes('年付') ? 'bg-brand-purple' : 'bg-brand-cyan'
                  )} />
                  <span>{t.tier}</span>
                </div>
                <span className="font-mono">{t.c}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 收入 */}
        <div className="card !p-3">
          <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <TrendingUp size={12} className="text-brand-cyan" />
            收入统计
          </h3>
          <div className="space-y-1.5">
            <RevenueRow label="今日" value={stats.revenue.today} total={stats.revenue.total} color="text-brand-cyan" />
            <RevenueRow label="近 30 天" value={stats.revenue.month} total={stats.revenue.total} color="text-brand-purple" />
            <RevenueRow label="总累计" value={stats.revenue.total} total={stats.revenue.total} color="text-brand-gold" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* 最近用户 */}
        <div className="card !p-3">
          <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <UserPlus size={12} className="text-brand-cyan" />
            最近注册
          </h3>
          <div className="space-y-1.5">
            {users.slice(0, 5).map(u => (
              <div key={u.id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-purple to-brand-cyan flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                    {u.name?.[0] || 'U'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs truncate">{u.name}</div>
                    <div className="text-[10px] text-ink-3 font-mono">{u.phone}</div>
                  </div>
                </div>
                <div className="text-[10px] text-ink-3">
                  {new Date(u.created_at).toLocaleDateString('zh-CN')}
                </div>
              </div>
            ))}
            {users.length === 0 && <div className="text-center text-xs text-ink-3 py-2">暂无用户</div>}
          </div>
        </div>

        {/* 最近支付 */}
        <div className="card !p-3">
          <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
            <CreditCard size={12} className="text-brand-gold" />
            最近支付
          </h3>
          <div className="space-y-1.5">
            {payments.slice(0, 5).map(p => (
              <div key={p.id} className="flex items-center justify-between text-xs">
                <div className="min-w-0">
                  <div className="text-xs truncate">{p.name || p.phone}</div>
                  <div className="text-[10px] text-ink-3">{p.tier} · {p.method}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono text-brand-gold">¥{p.amount}</div>
                  <div className="text-[10px] text-ink-3">
                    {new Date(p.paid_at).toLocaleDateString('zh-CN')}
                  </div>
                </div>
              </div>
            ))}
            {payments.length === 0 && <div className="text-center text-xs text-ink-3 py-2">暂无支付</div>}
          </div>
        </div>
      </div>

      {/* 实时活动 */}
      <div className="card !p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold flex items-center gap-1.5">
            <Activity size={12} className="text-brand-cyan" />
            实时活动
            <span className="text-[10px] text-ink-3">最近登录</span>
          </h3>
          <button onClick={() => load(true)} className="text-[10px] text-ink-3 hover:text-ink-1">
            ↻ 刷新
          </button>
        </div>
        <div className="space-y-0.5">
          {logs.map(l => (
            <div key={l.id} className="flex items-center gap-2 text-[11px] py-1 px-1.5 rounded hover:bg-white/3">
              <div className={clsx(
                'w-1.5 h-1.5 rounded-full flex-shrink-0',
                l.success ? 'bg-brand-cyan' : 'bg-accent-danger'
              )} />
              <span className="text-ink-2 font-mono">{l.user_phone || l.phone || '—'}</span>
              <span className="text-ink-3 flex-1 truncate">
                {l.success ? '登录成功' : `失败(${l.fail_reason})`}
              </span>
              <span className="text-[10px] text-ink-3">
                {new Date(l.at).toLocaleString('zh-CN', { hour12: false })}
              </span>
            </div>
          ))}
          {logs.length === 0 && <div className="text-center text-xs text-ink-3 py-2">暂无活动</div>}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color, bg, sub, trend }) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={clsx('card !p-2.5 relative overflow-hidden', `bg-gradient-to-br ${bg}`)}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className={clsx('w-6 h-6 rounded-md bg-black/20 flex items-center justify-center', color)}>
          <Icon size={12} />
        </div>
        {trend && (
          <div className={clsx('text-[9px] font-mono flex items-center gap-0.5',
            trend.startsWith('+') ? 'text-brand-cyan' : 'text-accent-danger'
          )}>
            {trend.startsWith('+') ? <ArrowUp size={8} /> : <ArrowDown size={8} />}
            {trend}
          </div>
        )}
      </div>
      <div className="text-[10px] text-ink-3 mb-0.5">{label}</div>
      <div className={clsx('text-lg font-bold font-mono leading-tight', color)}>{value}</div>
      {sub && <div className="text-[9px] text-ink-3 mt-0.5">{sub}</div>}
    </motion.div>
  );
}

function ProgressBar({ label, value, total, color }) {
  const pct = total > 0 ? (value / total * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-ink-2">{label}</span>
        <span className="font-mono">{value} <span className="text-ink-3">({pct.toFixed(0)}%)</span></span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5 }}
          className={clsx('h-full rounded-full', color)}
        />
      </div>
    </div>
  );
}

function RevenueRow({ label, value, total, color }) {
  const pct = total > 0 ? (value / total * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-0.5">
        <span className="text-ink-2">{label}</span>
        <span className={clsx('font-mono font-semibold', color)}>¥{value.toFixed(2)}</span>
      </div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all', color.replace('text-', 'bg-'))}
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
